import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';

type WebserverConfig = { http?: boolean };
type CertState = { isDefault?: boolean };
type CertData = { pubKey?: number[] | string[] };

export default function WebUiMaintenanceWebserverPage() {
  const [http, setHttp] = React.useState(false);
  const [certState, setCertState] = React.useState('loading...');
  const [privateKeyFile, setPrivateKeyFile] = React.useState<File | null>(null);
  const [publicKeyFile, setPublicKeyFile] = React.useState<File | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    const [cfg, cert] = await Promise.all([
      postWebUiApi<WebserverConfig>({
        service: 'webserver',
        access: 'get',
        command: 'get config',
        parameters: {},
      }),
      postWebUiApi<CertState>({
        service: 'webserver',
        access: 'get',
        command: 'is default',
        parameters: {},
      }),
    ]);
    setHttp(Boolean(cfg.http));
    setCertState(cert.isDefault ? 'Factory Certificate' : 'User Certificate');
  }, []);

  React.useEffect(() => {
    let alive = true;
    refresh()
      .then(() => alive && setError(null))
      .catch((err) => alive && setError((err as Error).message));
    return () => {
      alive = false;
    };
  }, [refresh]);

  const run = React.useCallback(async (fn: () => Promise<void>, ok: string) => {
    try {
      setLoading(true);
      await fn();
      setMessage(ok);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <WebUiPageWrapper titleList={['Maintenance', 'Webserver']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/2 flex-col gap-2">
          <h4 className="font-bold">Webserver Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Parameter</th>
                <th>Setting</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Parameter</td>
                <td>Setting</td>
              </tr>
              <tr className="webui-table-row-content">
                <td>http active</td>
                <td>
                  <WebUiInput
                    type="checkbox"
                    checked={http}
                    onChange={(e) => setHttp(e.target.checked)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={loading}
              onClick={() =>
                run(async () => {
                  await postWebUiApi({
                    service: 'webserver',
                    access: 'set',
                    command: 'set config',
                    parameters: { http },
                  });
                  await refresh();
                }, 'Webserver setting updated.')
              }
            >
              Set Webserver Settings
            </WebUiButton>
          </div>
        </div>

        <div className="flex w-1/2 flex-col gap-2">
          <h4 className="font-bold">Certificates</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Description</th>
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Used Certificate</td>
                <td>
                  <div className="flex items-center justify-between gap-2">
                    <div>{certState}</div>
                    <WebUiButton
                      disabled={loading}
                      onClick={() => {
                        refresh().catch(() => {});
                      }}
                    >
                      refresh
                    </WebUiButton>
                  </div>
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Private Key</td>
                <td>
                  <WebUiInput
                    type="file"
                    inputSize="sm"
                    onChange={(e) =>
                      setPrivateKeyFile(e.target.files?.[0] ?? null)
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Public Certificate Chain</td>
                <td>
                  <WebUiInput
                    type="file"
                    inputSize="sm"
                    onChange={(e) =>
                      setPublicKeyFile(e.target.files?.[0] ?? null)
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="flex flex-col gap-2">
            <WebUiButton
              disabled={loading || !privateKeyFile || !publicKeyFile}
              onClick={() =>
                run(async () => {
                  const [privKey, pubKey] = await Promise.all([
                    privateKeyFile?.text() ?? '',
                    publicKeyFile?.text() ?? '',
                  ]);
                  await postWebUiApi({
                    service: 'webserver',
                    access: 'set',
                    command: 'upload',
                    parameters: { privKey, pubKey },
                  });
                  await refresh();
                }, 'Certificates uploaded.')
              }
            >
              Upload &amp; Change Certificates
            </WebUiButton>
            <WebUiButton
              disabled={loading}
              onClick={() =>
                run(async () => {
                  const cert = await postWebUiApi<CertData>({
                    service: 'webserver',
                    access: 'get',
                    command: 'public certificate',
                    parameters: {},
                  });
                  const bytes = (cert.pubKey ?? []).map((v) =>
                    typeof v === 'string' ? v.charCodeAt(0) : v,
                  );
                  const blob = new Blob([new Uint8Array(bytes)], {
                    type: 'application/octet-stream',
                  });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'cert.pem';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                }, 'Certificate downloaded.')
              }
            >
              Download Public Certificate Chain
            </WebUiButton>
            <WebUiButton
              disabled={loading}
              onClick={() =>
                run(async () => {
                  await postWebUiApi({
                    service: 'webserver',
                    access: 'set',
                    command: 'reset',
                    parameters: {},
                  });
                  await refresh();
                }, 'Certificate reset.')
              }
            >
              Remove User Certificates
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
